import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import styled from "styled-components"

import { fetchPutStudentProfile, PutStudentProfileBodyMap } from "~/api/student/profile/put"
import Selectbox from "~/components/atoms/Selectbox"
import Typography from "~/components/atoms/Typography"
import Dialog from "~/components/molecules/Dialog"
import { Color } from "~/consts/style-mixins"
import { useComputedIfNotNull } from "~/hooks/useComputedIfNotNull"
import { useStores } from "~/stores/StoreProvider"

import ScoutIcon from "./scout.svg"

type ModalState = "hide" | "unknown-graduates-in" | "match-23" | "not-match-23"

type Props = {
  onClose?: (result?: "show-detail") => void
  mockFn?: () => Promise<void>
  mockModalState?: ModalState
}

export const Campaign2021Modal: React.VFC<Props> = ({ onClose, mockFn, mockModalState }) => {
  const { profileStore } = useStores()

  const { register, getValues, watch } = useForm<PutStudentProfileBodyMap["name_graduates_in"]>()

  const [isLoading, setIsLoading] = useState(false)
  const [_modalState, setModalState] = useState<ModalState>("hide")
  const modalState = useMemo(() => mockModalState || _modalState, [mockModalState, _modalState])

  const { isUnknownGraduatesIn } = useComputedIfNotNull(profileStore.json, ({ basic_info: { name_graduates_in } }) => {
    return { isUnknownGraduatesIn: name_graduates_in.graduates_in == null }
  })

  const onCloseClick = useCallback(() => {
    setModalState("hide")
    onClose?.()
  }, [])

  const onClickDetail = useCallback(() => {
    setModalState("hide")
    onClose?.("show-detail")
  }, [])

  const onClickSave = useCallback(async () => {
    const resultGraduatesIn = getValues("graduates_in")
    setIsLoading(true)
    if (mockFn) {
      await mockFn()
      setModalState("match-23")
      setIsLoading(false)
      return
    }

    await fetchPutStudentProfile({
      name_graduates_in: {
        graduates_in: resultGraduatesIn,
      },
    })
    await profileStore.fetchProfileDetail()

    setIsLoading(false)
    setModalState(resultGraduatesIn === 2023 ? "match-23" : "not-match-23")
  }, [mockFn])

  useEffect(() => {
    if (isUnknownGraduatesIn) {
      setModalState("unknown-graduates-in")
    } else {
      setModalState("match-23")
    }
  }, [])

  if (modalState === "match-23") {
    return (
      <Dialog
        open
        heading=""
        subAction={{ label: "閉じる", action: onCloseClick }}
        mainAction={{ label: "詳しくみる", action: onClickDetail }}
        vertical
      >
        <StylesContainer>
          <div className="sub-heading">23卒限定キャンペーン</div>
          <StyledSvgContainer>
            <ScoutIcon />
          </StyledSvgContainer>
          <Typography type="Heading">
            プロフィール入力で
            <br />
            Amazonギフト券3,000円が
            <br />
            必ず貰えるキャンペーン実施中！
          </Typography>

          <p>
            プロフィールを充実させると
            <br />
            本選考スカウトが届くようになります
          </p>
        </StylesContainer>
      </Dialog>
    )
  }

  if (modalState === "unknown-graduates-in") {
    return (
      <Dialog
        open
        heading=""
        mainAction={{
          label: "保存する",
          action: onClickSave,
          isLoading,
          disabled: !Boolean(watch("graduates_in")),
        }}
        vertical
      >
        <StylesContainer>
          <div className="sub-heading">23卒限定キャンペーン</div>
          <StyledSvgContainer>
            <ScoutIcon />
          </StyledSvgContainer>
          <Typography type="Heading">
            プロフィール入力で
            <br />
            Amazonギフト券3,000円が
            <br />
            必ず貰えるキャンペーン実施中！
          </Typography>
          <p>
            プロフィールを充実させると
            <br />
            本選考スカウトが届くようになります
          </p>
          <Typography type="Label" colorProp={Color.Attention} className="graduates-in-text">
            あなたの卒業予定年を選択してください
          </Typography>
          <Selectbox
            options={[
              { text: "選択してください", value: "" },
              { text: "2022年卒", value: "2022" },
              { text: "2023年卒", value: "2023" },
              { text: "2024年卒", value: "2024" },
              { text: "2025年卒", value: "2025" },
              { text: "2026年卒", value: "2026" },
              { text: "2027年卒", value: "2027" },
            ]}
            selectProps={register("graduates_in", { valueAsNumber: true })}
          />
        </StylesContainer>
      </Dialog>
    )
  }

  if (modalState === "not-match-23") {
    return (
      <Dialog
        open
        heading="卒業年の登録が完了しました"
        mainAction={{ label: "閉じる", action: onCloseClick, only: true }}
        vertical
      >
        本キャンペーンは23卒限定のため<BoldText>キャンペーン対象外</BoldText>
        になりますが、プロフィールを埋めることであなたにマッチした長期インターンや本選考のスカウトが届きやすくなります。時間があるときにぜひ入力を進めましょう！
        <StyledSvgContainer>
          <ScoutIcon />
        </StyledSvgContainer>
      </Dialog>
    )
  }

  return null
}

const StylesContainer = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  align-items: center;
  text-align: center;
  .sub-heading {
    padding: 4px 24px;
    font-weight: bold;
    color: ${Color.Primary};
    border: 1px solid ${Color.Primary};
    border-radius: 8px;
  }
  .graduates-in-text {
    margin-top: 8px;
  }
`

const StyledSvgContainer = styled.div`
  display: grid;
  place-content: center;
`

const BoldText = styled.span`
  font-weight: bold;
  color: ${Color.Attention};
`
